import React from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  IconInfoCircle,
  IconSettings,
  IconLink,
  IconPhoto,
  IconCalendar,
  IconDatabase,
  IconBuilding,
} from '@tabler/icons-react';
import type { Survey } from '../types';

interface SurveyFormFieldsProps {
  surveyName: string;
  surveyDescription: string;
  surveyLink: string;
  surveyStartDate: string;
  surveyEndDate: string;
  surveyDataReward: string;
  surveyBrandId: string;
  surveyIconUrl: string;
  surveyIsActive: boolean;
  brandOptions: string[];
  brandOptionLabels?: Record<string, string>;
  validationErrors: {
    startDate?: string;
    endDate?: string;
  };
  minStartDate?: string;
  minEndDate?: string;
  editingSurvey: Survey | null;
  expandedAccordion: string | false;
  onAccordionChange: (panel: string | false) => void;
  onSurveyNameChange: (value: string) => void;
  onSurveyDescriptionChange: (value: string) => void;
  onSurveyLinkChange: (value: string) => void;
  onSurveyStartDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSurveyEndDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSurveyDataRewardChange: (value: string) => void;
  onSurveyBrandIdChange: (value: string) => void;
  onSurveyIconUrlChange: (value: string) => void;
  onSurveyIsActiveChange: (value: boolean) => void;
}

export function SurveyFormFields({
  surveyName,
  surveyDescription,
  surveyLink,
  surveyStartDate,
  surveyEndDate,
  surveyDataReward,
  surveyBrandId,
  surveyIconUrl,
  surveyIsActive,
  brandOptions,
  brandOptionLabels,
  validationErrors,
  minStartDate,
  minEndDate,
  editingSurvey,
  expandedAccordion,
  onAccordionChange,
  onSurveyNameChange,
  onSurveyDescriptionChange,
  onSurveyLinkChange,
  onSurveyStartDateChange,
  onSurveyEndDateChange,
  onSurveyDataRewardChange,
  onSurveyBrandIdChange,
  onSurveyIconUrlChange,
  onSurveyIsActiveChange,
}: SurveyFormFieldsProps) {
  return (
    <Accordion
      value={expandedAccordion === 'panel1' ? ['panel1'] : []}
      onValueChange={(val) => onAccordionChange(val.includes('panel1') ? 'panel1' : false)}
      className="mb-2 rounded-lg border shadow-sm"
    >
      <AccordionItem value="panel1" className="border-none">
        <AccordionTrigger className="px-3 hover:bg-muted/50 hover:no-underline">
          <div className="flex items-center gap-2">
            <IconInfoCircle className="size-5 text-primary" />
            <span className="text-lg font-semibold">Survey Information</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <div className="mb-4">
            <div className="mb-2 flex items-center gap-2">
              <h6 className="text-base font-semibold">Basic Information</h6>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="survey-name">Survey Name *</Label>
                <Input
                  id="survey-name"
                  value={surveyName}
                  onChange={(e) => onSurveyNameChange(e.target.value)}
                  placeholder="Enter a descriptive name for this survey"
                  className="mt-1"
                />
                <p className="mt-1 text-xs text-muted-foreground">This name will be displayed to users</p>
              </div>
              <div>
                <Label htmlFor="survey-desc">Description *</Label>
                <Input
                  id="survey-desc"
                  value={surveyDescription}
                  onChange={(e) => onSurveyDescriptionChange(e.target.value)}
                  placeholder="Brief description of the survey"
                  className="mt-1"
                />
                <p className="mt-1 text-xs text-muted-foreground">Provide context about what this survey is for</p>
              </div>
            </div>
          </div>

          <Separator className="my-3" />

          <div className="mb-4">
            <div className="mb-2 flex items-center gap-2">
              <IconSettings className="size-4 text-primary" />
              <h6 className="text-base font-semibold">Configuration</h6>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="data-reward">Data Reward</Label>
                <div className="relative mt-1">
                  <IconDatabase className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="data-reward"
                    value={surveyDataReward}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '' || raw === '-') {
                        onSurveyDataRewardChange(raw);
                        return;
                      }
                      const n = parseFloat(raw);
                      if (!Number.isNaN(n) && n < 0) {
                        onSurveyDataRewardChange('0');
                        return;
                      }
                      onSurveyDataRewardChange(raw);
                    }}
                    type="number"
                    placeholder="0"
                    min={0}
                    step={1}
                    className="pl-9 pr-10"
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">MB</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Reward amount (MB) for completing the survey</p>
              </div>
              <div>
                <Label htmlFor="brand-id">Brand ID</Label>
                <div className="relative mt-1">
                  <Select
                    value={
                      brandOptions.length === 0
                        ? undefined
                        : brandOptions.includes(surveyBrandId)
                          ? surveyBrandId
                          : brandOptions[0]
                    }
                    onValueChange={(newValue) => {
                      if (newValue != null && newValue !== '') {
                        onSurveyBrandIdChange(newValue);
                      }
                    }}
                  >
                    <SelectTrigger id="brand-id" className="w-full">
                      <div className="flex items-center gap-2">
                        <IconBuilding className="size-4 text-muted-foreground" />
                        <SelectValue placeholder="Select a brand" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {brandOptions.length === 0 ? (
                        <SelectItem value="__none" disabled>No brands available</SelectItem>
                      ) : (
                        brandOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {brandOptionLabels?.[option] ?? option}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Associate this survey with a specific brand</p>
              </div>
              <div>
                <Label>Status</Label>
                <div className="mt-1 flex h-9 items-center justify-between rounded-md border px-3">
                  <span className="text-sm font-medium">
                    {surveyIsActive ? 'Active' : 'Inactive'}
                  </span>
                  <Switch
                    checked={surveyIsActive}
                    onCheckedChange={onSurveyIsActiveChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator className="my-3" />

          <div className="mb-4">
            <div className="mb-2 flex items-center gap-2">
              <IconLink className="size-4 text-primary" />
              <h6 className="text-base font-semibold">URLs & Links</h6>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="survey-link">Survey Link *</Label>
                <div className="relative mt-1">
                  <IconLink className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="survey-link"
                    value={surveyLink}
                    readOnly
                    disabled
                    placeholder="https://surveys.megsapp.com/{id}/"
                    className="overflow-x-auto whitespace-nowrap bg-muted/30 pl-9"
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {editingSurvey ? 'The public URL for this survey' : 'Auto-generated from survey ID'}
                </p>
              </div>
              <div>
                <Label htmlFor="icon-url">Icon URL</Label>
                <div className="relative mt-1">
                  <IconPhoto className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="icon-url"
                    value={surveyIconUrl}
                    readOnly
                    disabled
                    placeholder="https://example.com/icon.png"
                    className="overflow-x-auto whitespace-nowrap bg-muted/30 pl-9"
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">URL to the survey icon image</p>
              </div>
            </div>
          </div>

          <Separator className="my-3" />

          <div>
            <div className="mb-2 flex items-center gap-2">
              <IconCalendar className="size-4 text-primary" />
              <h6 className="text-base font-semibold">Schedule</h6>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="start-date">Start Date *</Label>
                <div className="relative mt-1">
                  <Input
                    id="start-date"
                    type="date"
                    value={surveyStartDate}
                    onChange={onSurveyStartDateChange}
                    min={minStartDate}
                    className={cn(validationErrors.startDate && 'border-destructive')}
                  />
                </div>
                <p className={cn(
                  'mt-1 text-xs',
                  validationErrors.startDate ? 'text-destructive' : 'text-muted-foreground'
                )}>
                  {validationErrors.startDate || "When the survey becomes available"}
                </p>
              </div>
              <div>
                <Label htmlFor="end-date">End Date *</Label>
                <div className="relative mt-1">
                  <Input
                    id="end-date"
                    type="date"
                    value={surveyEndDate}
                    onChange={onSurveyEndDateChange}
                    min={minEndDate}
                    className={cn(validationErrors.endDate && 'border-destructive')}
                  />
                </div>
                <p className={cn(
                  'mt-1 text-xs',
                  validationErrors.endDate ? 'text-destructive' : 'text-muted-foreground'
                )}>
                  {validationErrors.endDate || "When the survey expires"}
                </p>
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
